import styled, { css } from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Checkbox from '@/components/Checkbox/Checkbox';
import FavoriteToggle from '@/components/Common/FavoriteToggle';
import Typo from '@/components/Typography/Typo';
import { useFeedbackTerminology } from '@/entities/workspace';
import {
  ChartTypeIconDic,
  ChartTypeMap,
  getChartFieldLabel,
} from '@/features/(charts)/chart-renderer/model/chart-maps';
import TicketLabel from '@/features/Commons/Ticket/TicketLabel';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { getTimezoneDayjs } from '@/utils/time';

type ChartSelectItemProps = {
  chart: ChartItem;
  onClick: (alias: string) => void;
  disabled?: boolean;
  selected: boolean;
  multiSelect?: boolean;
};

const ChartSelectItem = (props: ChartSelectItemProps) => {
  const color = useColor();
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const { multiSelect = true } = props;
  const { currWorkspaceMembers } = useWorkspaceStore((state) => ({
    currWorkspaceMembers: state.currWorkspaceMembers,
  }));
  const getChartCreator = (creatorId: string): string => {
    const creator = currWorkspaceMembers.find(
      (member) => member.user_id === creatorId,
    );
    return creator?.first_name || 'Unknown';
  };

  let field: string | undefined = '';
  field = replaceFeedbackTerminology(
    getChartFieldLabel(props.chart.field.type),
  );
  const Icon = ChartTypeIconDic[props.chart.type];

  return (
    <Style
      onClick={() => !props.disabled && props.onClick(props.chart.alias)}
      disabled={props.disabled}
      multiSelect={multiSelect}
      selected={props.selected}
    >
      {multiSelect && <Checkbox checked={props.selected} />}
      <span
        style={{
          fontSize: '18px',
          color: color.gray700,
        }}
      >
        <Icon />
      </span>

      <VFlex g={4}>
        <Flex a="center" g={4}>
          <Typo type="label2" weight="sb" color="gray600">
            {props.chart.name}
          </Typo>
          <FavoriteToggle value={props.chart.starred} readonly />
          {props.disabled && <TicketLabel>Already Added</TicketLabel>}
        </Flex>

        <Flex a="center" g={4}>
          <Typo type="label3" weight="r">
            {ChartTypeMap.get(props.chart.type)}
          </Typo>
          <Typo type="label3" weight="r">
            ﹒
          </Typo>
          <Typo type="label3" weight="r">
            {getTimezoneDayjs(props.chart.updated_at).fromNow()}
          </Typo>
          <Typo type="label3" weight="r">
            ﹒
          </Typo>
          <Typo type="label3" weight="r">
            {getChartCreator(props.chart.updated_by)}
          </Typo>
          <Typo type="label3" weight="r">
            ﹒
          </Typo>
          <Typo type="label3" weight="r">
            {field}
          </Typo>
        </Flex>
      </VFlex>
    </Style>
  );
};

const Style = styled(Flex).attrs({
  a: 'center',
  g: 12,
})<{
  disabled?: boolean;
  multiSelect?: boolean;
  selected?: boolean;
}>`
  padding: 12px;
  border-radius: 12px;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};

  &:hover {
    background-color: ${(props) =>
      props.disabled ? 'unset' : props.theme.colors.hover};
  }

  ${({ multiSelect, selected, theme }) =>
    !multiSelect &&
    css`
      padding: 8px 16px 12px;
      transition: all 0.2s;
      border: 1px solid ${selected ? theme.colors.primary100 : 'transparent'};
    `}
`;

export default ChartSelectItem;
